import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { UserButton, useAuth } from "@clerk/clerk-react";
import { useAuthContext } from "../contexts/AuthContext";
import { OrganizationSwitcher } from "./OrganizationSwitcher";
import { BetaFeedbackDialog } from "./BetaFeedbackDialog";
import { 
  LayoutDashboard, 
  Globe, 
  BarChart3, 
  Settings, 
  Menu,
  X
} from "lucide-react";
import { useState } from "react";

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Domains', href: '/domains', icon: Globe },
  { name: 'Analytics', href: '/stats', icon: BarChart3 },
];

export default function Layout() {
  const { isSignedIn } = useAuth();
  const { user } = useAuthContext();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const feedbackWorkflow = getShellFeedbackWorkflow(location.pathname, location.search);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
              <div className="flex items-center">
                <Link to="/" className="flex items-center space-x-1">
                  <span className="text-lg font-bold text-gray-900">dn</span>
                  <svg 
                    viewBox="0 0 100 100" 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="w-4 h-4 text-indigo-600" 
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M50.002 0C30.763 0 15 15.718 15 34.902c0 7.432 2.374 14.34 6.392 20.019L45.73 96.994c3.409 4.453 5.675 3.607 8.51-.235l26.843-45.683c.542-.981.967-2.026 1.338-3.092A34.446 34.446 0 0 0 85 34.902C85 15.718 69.24 0 50.002 0zm0 16.354c10.359 0 18.597 8.218 18.597 18.548c0 10.33-8.238 18.544-18.597 18.544c-10.36 0-18.601-8.215-18.601-18.544c0-10.33 8.241-18.548 18.6-18.548z" />
                  </svg>
                </Link>
              </div>

            {/* Desktop Navigation */}
            {isSignedIn && (
              <nav className="hidden md:flex space-x-8">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? 'bg-indigo-50 text-indigo-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.name}</span>
                    </NavLink>
                  );
                })}
              </nav>
            )}

            {/* Right side */}
            <div className="flex items-center space-x-4">
              {isSignedIn ? (
                <>
                  {/* Organization Switcher */}
                  <div className="hidden md:block">
                    <OrganizationSwitcher />
                  </div>

                  <div className="hidden md:block">
                    <BetaFeedbackDialog
                      variant="outline"
                      compact
                      workflow={feedbackWorkflow}
                      buttonLabel="Beta Feedback"
                    />
                  </div>
                  
                  {/* Mobile menu button */}
                  <button
                    type="button"
                    className="md:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  >
                    {mobileMenuOpen ? (
                      <X className="w-5 h-5" />
                    ) : (
                      <Menu className="w-5 h-5" />
                    )}
                  </button>

                  {/* User info and avatar */}
                  <div className="flex items-center space-x-3">
                    {user && (
                      <div className="hidden sm:block text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {user.username || 'User'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {user.email}
                        </p>
                      </div>
                    )}
                    <UserButton 
                      appearance={{
                        elements: {
                          avatarBox: "w-10 h-10 rounded-full ring-2 ring-white shadow-lg",
                          userButtonPopoverCard: "shadow-xl border border-gray-200 rounded-xl",
                          userButtonPopoverActions: "p-4"
                        }
                      }}
                    >
                      <UserButton.MenuItems>
                        <UserButton.Link
                          label="Settings"
                          labelIcon={<Settings className="w-4 h-4" />}
                          href="/settings"
                        />
                      </UserButton.MenuItems>
                    </UserButton>
                  </div>
                </>
              ) : (
                <div className="flex items-center space-x-3">
                  <Link 
                    to="/sign-in" 
                    className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link 
                    to="/sign-up" 
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Navigation */}
          {isSignedIn && mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 py-4">
              {/* Organization Switcher for Mobile */}
              <div className="mb-4">
                <OrganizationSwitcher />
              </div>
              
              <nav className="space-y-2">
                <div className="px-3 py-2">
                  <BetaFeedbackDialog
                    variant="ghost"
                    workflow={feedbackWorkflow}
                    buttonLabel="Beta Feedback"
                  />
                </div>
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={`flex items-center space-x-3 px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? 'bg-indigo-50 text-indigo-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{item.name}</span>
                    </NavLink>
                  );
                })}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Outlet />
        </div>
      </main>

    </div>
  );
}

function getShellFeedbackWorkflow(pathname: string, search: string): string {
  if (pathname.startsWith("/domains")) {
    return "domains / app shell";
  }

  if (pathname.startsWith("/stats")) {
    return "analytics / app shell";
  }

  if (pathname.startsWith("/onboarding")) {
    return "onboarding / app shell";
  }

  if (pathname.startsWith("/settings")) {
    const tab = new URLSearchParams(search).get("tab");
    if (tab === "providers") {
      return "providers / app shell";
    }
    if (tab === "api-keys") {
      return "api keys / app shell";
    }
    return "settings / app shell";
  }

  if (pathname.startsWith("/dashboard")) {
    return "dashboard / app shell";
  }

  return "app shell";
}
